import { Pagination } from '@core/models/pagination.model';
import {
  IAccountMetadata,
  IAccountRMMetadata,
  IRoles,
  ITableFilter,
  TableOperation,
  TableStateModel,
} from './table.state.model';

const ACTION_SCOPE = '[Table]';

export namespace TableActions {
  // Generic table actions
  export class LoadTableData {
    static readonly type = `${ACTION_SCOPE} Load Table Data`;
    constructor(
      public payload: {
        tableKey: string; // 'approval', 'users', 'transactions', etc.
        params: Pagination;
        filters?: ITableFilter[];
      }
    ) {}
  }

  export class LoadTableDataSuccess {
    static readonly type = `${ACTION_SCOPE} Load Table Data Success`;
    constructor(
      public payload: {
        tableKey: string;
        data: any[];
        totalCount?: number;
      }
    ) {}
  }

  export class LoadTableDataFailure {
    static readonly type = `${ACTION_SCOPE} Load Table Data Failure`;
    constructor(
      public payload: {
        tableKey: string;
        error: string;
      }
    ) {}
  }

  export class UpdateTableItem {
    static readonly type = `${ACTION_SCOPE} Update Table Item`;
    constructor(
      public payload: {
        tableKey: string;
        itemId: string;
        updates: Record<string, any>;
      }
    ) {}
  }

  export class DeleteTableItem {
    static readonly type = `${ACTION_SCOPE} Delete Table Item`;
    constructor(
      public payload: {
        tableKey: string;
        itemId: string;
      }
    ) {}
  }

  export class PerformTableOperation {
    static readonly type = `${ACTION_SCOPE} Perform Table Operation`;
    constructor(
      public payload: {
        tableKey: string;
        itemId: string;
        operation: TableOperation;
        data?: any;
      }
    ) {}
  }

  export class SetTableFilters {
    static readonly type = `${ACTION_SCOPE} Set Table Filters`;
    constructor(
      public payload: {
        tableKey: string;
        filters: ITableFilter[];
      }
    ) {}
  }

  export class SetTablePagination {
    static readonly type = `${ACTION_SCOPE} Set Table Pagination`;
    constructor(
      public payload: {
        tableKey: string;
        pagination: Pagination;
      }
    ) {}
  }

  export class SelectTableItems {
    static readonly type = `${ACTION_SCOPE} Select Table Items`;
    constructor(
      public payload: {
        tableKey: string;
        selectedItems: any[];
      }
    ) {}
  }

  export class ClearTableData {
    static readonly type = `${ACTION_SCOPE} Clear Table Data`;
    constructor(
      public payload: {
        tableKey: string;
      }
    ) {}
  }

  // Backward compatibility - Approval specific actions
  export class LoadApprovals {
    static readonly type = `${ACTION_SCOPE} Load Approvals`;
    constructor(public payload: { params: Pagination }) {}
  }

  export class LoadApprovalsSuccess {
    static readonly type = `${ACTION_SCOPE} Load Approvals Success`;
    constructor(public payload: { approvals: any[] }) {}
  }

  export class LoadApprovalsFailure {
    static readonly type = `${ACTION_SCOPE} Load Approvals Failure`;
    constructor(public payload: { error: string }) {}
  }

  export class ApproveApproval {
    static readonly type = `${ACTION_SCOPE} Approve Approval`;
    constructor(public payload: { approvalId: string }) {}
  }

  export class RejectApproval {
    static readonly type = `${ACTION_SCOPE} Reject Approval`;
    constructor(public payload: { approvalId: string }) {}
  }

  export class UpdateDocumentStatus {
    static readonly type = `${ACTION_SCOPE} Update Document Status`;
    constructor(
      public payload: { documentId: string; status: 'approved' | 'rejected' }
    ) {}
  }

  // Account specific actions
  export class GetAccountsMetadata {
    static readonly type = `${ACTION_SCOPE} Get Accounts Metadata`;
    constructor() {}
  }
  export class GetAccountsMetadataSuccess {
    static readonly type = `${ACTION_SCOPE} Get Accounts Metadata Success`;
    constructor(public payload: { accountMetadata: IAccountMetadata }) {}
  }

  export class GetAccountsMetadataFailure {
    static readonly type = `${ACTION_SCOPE} Get Accounts Metadata Failure`;
    constructor(public payload: { error: string }) {}
  }

  export class GetAccountsRMMetadata {
    static readonly type = `${ACTION_SCOPE} Get Accounts-RM Metadata`;
    constructor() {}
  }
  export class GetAccountsRMMetadataSuccess {
    static readonly type = `${ACTION_SCOPE} Get Accounts-RM Metadata Success`;
    constructor(public payload: { accountRMMetadata: IAccountRMMetadata }) {}
  }

  export class GetAccountsRMMetadataFailure {
    static readonly type = `${ACTION_SCOPE} Get Accounts  RM Metadata Failure`;
    constructor(public payload: { error: string }) {}
  }
  export class FilterAccountRMList {
    static readonly type = `${ACTION_SCOPE} Filter Account RM List`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterAccountList {
    static readonly type = `${ACTION_SCOPE} Filter Account List`;
    constructor(public payload: { filter: string; table: string }) {}
  }

  export class BlockAccount {
    static readonly type = `${ACTION_SCOPE} Block Account`;
    constructor(public payload: { accountId: string }) {}
  }
  export class UnblockAccount {
    static readonly type = `${ACTION_SCOPE} Unblock Account`;
    constructor(public payload: { accountId: string }) {}
  }

  export class DeleteAccount {
    static readonly type = `${ACTION_SCOPE} Delete Account`;
    constructor(public payload: { accountId: string }) {}
  }

  // mobile users
  export class FilterMobileUsers {
    static readonly type = `${ACTION_SCOPE} Filter Mobile Users List`;
    constructor(public payload: { filter: string }) {}
  }

  // system users
  export class GetSystemUsersRoles {
    static readonly type = `${ACTION_SCOPE} Get System Users Roles`;
    constructor() {}
  }

  export class GetPermissions {
    static readonly type = `${ACTION_SCOPE} Get Permissions`;
    constructor() {}
  }

  export class GetSystemUsersRolesSuccess {
    static readonly type = `${ACTION_SCOPE} Get System Users Roles Success`;
    constructor(public payload: { roles: IRoles[] }) {}
  }

  export class GetSystemUsersRolesFailure {
    static readonly type = `${ACTION_SCOPE} Get System Users Roles Failure`;
    constructor(public payload: { error: string }) {}
  }

  export class FilterSystemUsers {
    static readonly type = `${ACTION_SCOPE} Filter System Users`;
    constructor(public payload: { filter: string }) {}
  }

  export class DeleteSystemUser {
    static readonly type = `${ACTION_SCOPE} Delete System User`;
    constructor(public payload: { adminId: string }) {}
  }

  // roles
  export class DeleteRole {
    static readonly type = `${ACTION_SCOPE} Delete Roles`;
    constructor(public payload: { roleId: string }) {}
  }

  export class FilterRoleList {
    static readonly type = `${ACTION_SCOPE} Filter role list`;
    constructor(public payload: { filter: string }) {}
  }

  export class UpdateRolePermission {
    static readonly type = `${ACTION_SCOPE} Update Roles permission`;
    constructor(
      public payload: { roleId: string; permission: any; isAdd: boolean }
    ) {}
  }
  // order

  export class FilterOrderList {
    static readonly type = `${ACTION_SCOPE} Filter order list`;
    constructor(public payload: { filter: string }) {}
  }

  export class RejectOrder {
    static readonly type = `${ACTION_SCOPE} Reject Order`;
    constructor(public payload: { orderId: string }) {}
  }
  export class RejectMFTransaction {
    static readonly type = `${ACTION_SCOPE} Reject MF transaction`;
    constructor(public payload: { orderId: string }) {}
  }

  export class ApproveOrder {
    static readonly type = `${ACTION_SCOPE} Approve Order`;
    constructor(public payload: { orderId: string; value_date?: Date }) {}
  }

  export class ApproveMFTransaction {
    static readonly type = `${ACTION_SCOPE} Approve MF transaction`;
    constructor(public payload: { orderId: string; value_date?: Date }) {}
  }

  //  product
  export class UpdateMFProductStatus {
    static readonly type = `${ACTION_SCOPE} Update MF product status`;
    constructor(
      public payload: {
        productId: string;
        status: 'public' | 'private' | 'archived';
        tableKey: keyof TableStateModel;
      }
    ) {}
  }

  export class SearchTBillProduct {
    static readonly type = `${ACTION_SCOPE} Search TBill product`;
    constructor(public payload: { searchText: string }) {}
  }
  export class SearchBondsProduct {
    static readonly type = `${ACTION_SCOPE} Search Bonds product`;
    constructor(public payload: { searchText: string }) {}
  }

  export class SearchCPaperProduct {
    static readonly type = `${ACTION_SCOPE} Search Commercial paper product`;
    constructor(public payload: { searchText: string }) {}
  }

  // Transactions
  export class FilterWalletTransctionsList {
    static readonly type = `${ACTION_SCOPE} Filter wallet transaction list`;
    constructor(public payload: { filter: string }) {}
  }

  export class GetProvidusBalance {
    static readonly type = `${ACTION_SCOPE} Get Providus Balance`;
    constructor() {}
  }
  export class GetAccessBankBalance {
    static readonly type = `${ACTION_SCOPE} Get Access Bank Balance`;
    constructor() {}
  }
  export class GetVertoBalance {
    static readonly type = `${ACTION_SCOPE} Get Verto Balance`;
    constructor() {}
  }
  export class GetPaystackBalance {
    static readonly type = `${ACTION_SCOPE} Get Paystack Balance`;
    constructor() {}
  }

  // Notifications
  export class FilterNotificationLog {
    static readonly type = `${ACTION_SCOPE} Filter notification list`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterNotificationUsersList {
    static readonly type = `${ACTION_SCOPE} Filter notification users list`;
    constructor(public payload: { filter: string }) {}
  }

  // cash transaction
  export class FilterCashTransctionsList {
    static readonly type = `${ACTION_SCOPE} Filter cash transaction list`;
    constructor(public payload: { filter: string }) {}
  }

  // fx transaction
  export class FilterFxTransactionsList {
    static readonly type = `${ACTION_SCOPE} Filter fx transaction list`;
    constructor(public payload: { filter: string }) {}
  }

  export class GetFxSummary {
    static readonly type = `${ACTION_SCOPE} Get FX Summary`;
    constructor() {}
  }

  export class GetWalletCashBalance {
    static readonly type = `${ACTION_SCOPE} Get wallet cash Balance`;
    constructor() {}
  }
  export class GetInvestmentCashBalance {
    static readonly type = `${ACTION_SCOPE} Get investment Balance`;
    constructor() {}
  }

  // referral
  export class FilterReferralList {
    static readonly type = `${ACTION_SCOPE} Filter Referral List`;
    constructor(public payload: { filter: string }) {}
  }

  export class GetReferralsMetadata {
    static readonly type = `${ACTION_SCOPE} Get Referral Metadata`;
    constructor() {}
  }
  export class FilterIdentityLogList {
    static readonly type = `${ACTION_SCOPE} Filter Identity Log List`;
    constructor(public payload: { filter: string }) {}
  }

  // update approvals
  export class ApproveUpdate {
    static readonly type = `${ACTION_SCOPE} Approve Update`;
    constructor(
      public payload: { updateId: string; action: 'approved' | 'rejected' }
    ) {}
  }

  export class GetUpdatesMetadata {
    static readonly type = `${ACTION_SCOPE} Get Updates Metadata`;
    constructor() {}
  }

  export class FilterApprovalList {
    static readonly type = `${ACTION_SCOPE} Filter Approval List`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterAuditLogList {
    static readonly type = `${ACTION_SCOPE} Filter Audit Log List`;
    constructor(public payload: { filter: string }) {}
  }

  // MFD TRX Approvals
  export class ApproveOrRejectOrder {
    static readonly type = `${ACTION_SCOPE} Update MFD TRX approval`;
    constructor(
      public payload: { id: string; action: 'approved' | 'rejected' }
    ) {}
  }

  export class FilterOrderApprovalList {
    static readonly type = `${ACTION_SCOPE} Filter MFD TRX approval list`;
    constructor(public payload: { filter: string }) {}
  }

  // reports
  export class FilterActiveClientsList {
    static readonly type = `${ACTION_SCOPE} Filter Active Clients list`;
    constructor(
      public payload: { filter: string; type: 'active' | 'inactive' }
    ) {}
  }

  export class FilterGeneralReportList {
    static readonly type = `${ACTION_SCOPE} Filter General Report list`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterFundedClientsList {
    static readonly type = `${ACTION_SCOPE} Filter Funded Clients list`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterPortfolioReportList {
    static readonly type = `${ACTION_SCOPE} Filter portfolio report list`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterMNMMFStatementsList {
    static readonly type = `${ACTION_SCOPE} Filter MNMMF statements list`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterBenchmarkList {
    static readonly type = `${ACTION_SCOPE} Filter Benchmark List`;
    constructor(public payload: { filter: string }) {}
  }

  export class FilterFacialRecognitionLogList {
    static readonly type = `${ACTION_SCOPE} Filter Facial Recognition Log List`;
    constructor(public payload: { filter: string }) {}
  }
}
